// Country helpers. Salesforce stores full English names; we render with a
// flag emoji where we know the ISO-2 code. Any country we don't know about
// falls back to the bare name (no flag) — better than showing nothing.

// Names we've seen in innodia-prod 2026-05-05 plus a buffer for likely
// additions. Anything outside this map renders without a flag — add an
// entry when a new country appears.
const NAME_TO_ISO: Record<string, string> = {
  Argentina: "AR", Australia: "AU", Austria: "AT", Belgium: "BE",
  Brazil: "BR", Bulgaria: "BG", Canada: "CA", Colombia: "CO",
  Czechia: "CZ", "Czech Republic": "CZ", Denmark: "DK", Finland: "FI",
  France: "FR", Germany: "DE", Greece: "GR", Guatemala: "GT",
  Hungary: "HU", India: "IN", Indonesia: "ID", Ireland: "IE",
  Israel: "IL", Italy: "IT", Japan: "JP", Kenya: "KE",
  Lebanon: "LB", Liberia: "LR", Lithuania: "LT", Luxembourg: "LU",
  Mexico: "MX", Morocco: "MA", Netherlands: "NL", "The Netherlands": "NL",
  Nigeria: "NG", Norway: "NO", Pakistan: "PK", Philippines: "PH",
  Poland: "PL", Portugal: "PT", Romania: "RO", "Saudi Arabia": "SA",
  Slovakia: "SK", Slovenia: "SI", Spain: "ES", Sweden: "SE",
  Switzerland: "CH", Tanzania: "TZ", Turkey: "TR",
  UAE: "AE", "United Arab Emirates": "AE",
  Ukraine: "UA", "United Kingdom": "GB",
  "United States": "US", "United States of America": "US", USA: "US",
  Uruguay: "UY", Zimbabwe: "ZW",
};

/** ISO-2 → flag emoji via the Unicode regional-indicator construction.
 *  "GB" → 0x1F1EC + 0x1F1E7 → 🇬🇧.
 */
function isoToFlag(iso2: string): string {
  if (iso2.length !== 2) return "";
  const A = 0x41;
  const RIA = 0x1f1e6; // Regional Indicator Symbol Letter A
  const c = iso2.toUpperCase();
  return String.fromCodePoint(
    RIA + (c.charCodeAt(0) - A),
    RIA + (c.charCodeAt(1) - A),
  );
}

export function flagFor(value: string | null | undefined): string {
  if (!value) return "";
  // Accept either ISO-2 or full English name.
  if (value.length === 2 && /^[A-Za-z]{2}$/.test(value)) {
    return isoToFlag(value);
  }
  const iso = NAME_TO_ISO[value];
  return iso ? isoToFlag(iso) : "";
}

export function displayCountry(value: string | null | undefined): string {
  if (!value) return "—";
  // Backward-compat: a few callers may pass ISO-2.
  if (value.length === 2 && /^[A-Za-z]{2}$/.test(value)) {
    const entry = Object.entries(NAME_TO_ISO).find(
      ([, iso]) => iso === value.toUpperCase(),
    );
    return entry?.[0] ?? value;
  }
  return value;
}
